import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'
import { AchievementBadge, SkillWeight } from '../../content'
import { LearnProgress } from './learn-progress'

export const EarnedXp = pgTable(
  'earned_xp',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    learnProgressId: uuid().references(() => LearnProgress.id, { onDelete: 'set null' }),
    achievementBadgeId: uuid().references(() => AchievementBadge.id, { onDelete: 'set null' }),
    skillWeightId: uuid()
      .notNull()
      .references(() => SkillWeight.id, { onDelete: 'restrict' }),
    xpEarned: numericAsNumber().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),

    // DEAD FLAGS — both pairs below fed the deleted per-table producers. Nothing reads them:
    // earned_xp_daily and lesson_metrics_daily (badge XP included) are recomputed from these rows by
    // `compute<Metric>` (packages/trigger/src/metrics/). Kept until a migration drops them.

    // Earned XP daily tracking
    calculatedForEarnedXpDaily: boolean().default(false).notNull(),
    earnedXpDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Lesson metrics daily tracking — formerly drove badge rows (learnProgressId IS NULL) into
    // LessonMetricsDaily. The compute now reads badge XP on its createdAt day directly.
    calculatedForLessonMetricsDaily: boolean().default(false).notNull(),
    lessonMetricsDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),
  },
  (t) => [
    index('idx_earned_xp_user').on(t.userId),
    // Live today reads of earned_xp_daily / lesson metrics badge XP: a user's XP rows by creation time.
    index('idx_earned_xp_user_created').on(t.userId, t.createdAt),
    index('idx_earned_xp_learn_progress').on(t.learnProgressId),
    index('idx_earned_xp_achievement_badge').on(t.achievementBadgeId),
    index('idx_earned_xp_skill_weight').on(t.skillWeightId),
    index('idx_earned_xp_lesson_metrics_daily').on(t.calculatedForLessonMetricsDaily),
  ],
)

export const earnedXpRelations = relations(EarnedXp, ({ one }) => ({
  user: one(user, {
    fields: [EarnedXp.userId],
    references: [user.id],
  }),

  learnProgress: one(LearnProgress, {
    fields: [EarnedXp.learnProgressId],
    references: [LearnProgress.id],
  }),

  achievementBadge: one(AchievementBadge, {
    fields: [EarnedXp.achievementBadgeId],
    references: [AchievementBadge.id],
  }),

  skillWeight: one(SkillWeight, {
    fields: [EarnedXp.skillWeightId],
    references: [SkillWeight.id],
  }),
}))

export const InsertEarnedXpSchema = createInsertSchema(EarnedXp)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.xpEarned < 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'XP Earned must be greater than 0',
        path: ['xpEarned'],
      })
    }

    // XP must come from somewhere
    if (!data.learnProgressId && !data.achievementBadgeId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Either learnProgressId or achievementBadgeId must be provided',
        path: ['learnProgressId'],
      })
    }
  })

export const UpdateEarnedXpSchema = createUpdateSchema(EarnedXp)
  .partial()
  .omit({
    id: true,
    userId: true,
    learnProgressId: true,
    achievementBadgeId: true,
    skillWeightId: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.xpEarned && data.xpEarned < 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'XP Earned must be greater than 0',
        path: ['xpEarned'],
      })
    }
  })
export const SelectEarnedXpSchema = createSelectSchema(EarnedXp)

export type InsertEarnedXp = typeof EarnedXp.$inferInsert
export type UpdateEarnedXp = z.infer<typeof UpdateEarnedXpSchema>
export type SelectEarnedXp = typeof EarnedXp.$inferSelect
